import { useCallback, useLayoutEffect, useRef } from 'preact/hooks';
import { useEffect } from 'react';
import { Review } from 'types/review';

import { ReviewCard } from './components/ReviewCard';

import styles from './MasonryLayout.module.scss';

interface MasonryLayoutProps {
  reviews: Review[];
}

export function MasonryLayout({ reviews }: MasonryLayoutProps) {
  const ref = useRef<HTMLDivElement>(null);

  const resizeItem = useCallback(
    ($item: HTMLDivElement | null) => {
      const $grid = ref.current;

      if ($grid) {
        const rowHeight = parseInt(
          window.getComputedStyle($grid).getPropertyValue('grid-auto-rows'),
          10,
        );
        const rowGap = parseInt(
          window.getComputedStyle($grid).getPropertyValue('grid-row-gap'),
          10,
        );

        if ($item) {
          const $card = $item.querySelector('div');

          if ($card) {
            const rowSpan = Math.ceil(
              ($card.getBoundingClientRect().height + rowGap) /
                (rowHeight + rowGap),
            );

            $item.style.gridRowEnd = `span ${rowSpan}`;
          }
        }
      }
    },
    [ref],
  );

  const recalculateMasonry = useCallback(() => {
    const $grid = ref.current;

    if ($grid) {
      const items = $grid.querySelectorAll(
        'div.wbtxMasonryItem',
      ) as NodeListOf<HTMLDivElement>;

      items.forEach(($item) => resizeItem($item));
    }
  }, [ref, resizeItem]);

  useLayoutEffect(() => {
    recalculateMasonry();
    window.addEventListener('resize', recalculateMasonry);

    return () => {
      window.removeEventListener('resize', recalculateMasonry);
    };
  }, [recalculateMasonry, reviews]);

  useEffect(() => {
    const $grid = ref.current;

    const updateMasonry = (e: Event) => {
      if (e) {
        e.preventDefault();
        resizeItem(e.target as HTMLDivElement);
      }
    };

    if ($grid) {
      $grid.addEventListener('wbtxImageLoaded', updateMasonry);
    }

    return () => {
      if ($grid) {
        $grid.removeEventListener('wbtxImageLoaded', updateMasonry);
      }
    };
  }, [ref, resizeItem]);

  const reviewsMarkup = reviews.map((review) => (
    <ReviewCard key={review.id} review={review} />
  ));

  return (
    <div ref={ref} className={styles.ReviewsMasonry}>
      {reviewsMarkup}
    </div>
  );
}
